import { isValidIconId } from '../lib/icons';

/**
 * Validates and normalizes icon identifiers from AI generation
 */
export interface IconValidationResult {
  isValid: boolean;
  normalizedIcon?: string;
  fallbackIcon?: string;
  error?: string;
}

/**
 * Cache for validated icons to avoid repeated lookups
 */
const iconValidationCache = new Map<string, IconValidationResult>();

/**
 * Validates an icon identifier and returns a normalized version or fallback
 * @param iconId - The icon identifier from AI (e.g., 'si:react', 'react', 'mdi:server')
 * @returns Validation result with normalized icon or fallback
 */
export function validateAndNormalizeIcon(iconId: string): IconValidationResult {
  // Check cache first
  if (iconValidationCache.has(iconId)) {
    return iconValidationCache.get(iconId)!;
  }

  const result = performValidation(iconId);
  iconValidationCache.set(iconId, result);
  return result;
}

/**
 * Perform the actual validation
 */
function performValidation(iconId: string): IconValidationResult {
  // If already namespaced, try it directly
  if (iconId.includes(':')) {
    if (isValidIconId(iconId)) {
      return { isValid: true, normalizedIcon: iconId };
    }
    
    // Try to find a similar icon in the same source
    const similarIcon = findSimilarIcon(iconId);
    if (similarIcon) {
      return {
        isValid: false,
        normalizedIcon: similarIcon,
        error: `Icon '${iconId}' not found, using similar icon '${similarIcon}'`
      };
    }
  } else {
    // Unprefixed - default to SimpleIcons
    const siIcon = `si:${iconId}`;
    
    if (isValidIconId(siIcon)) {
      return { isValid: true, normalizedIcon: siIcon };
    }
    
    // Try to find a similar SimpleIcon
    const similarIcon = findSimilarIcon(siIcon);
    if (similarIcon) {
      return {
        isValid: false,
        normalizedIcon: similarIcon,
        error: `SimpleIcon '${iconId}' not found, using similar icon '${similarIcon}'`
      };
    }
  }

  // Fallback to a generic icon
  const fallbackIcon = 'si:files';
  return {
    isValid: false,
    fallbackIcon,
    error: `Icon '${iconId}' not found, using fallback icon '${fallbackIcon}'`
  };
}

/**
 * Find a similar icon by name variations
 */
function findSimilarIcon(iconId: string): string | null {
  const [source, name] = iconId.split(':', 2);
  
  const variations = [
    name.toLowerCase(),
    name.toLowerCase().replace(/[^a-z0-9]/g, ''),
    name.toLowerCase().replace(/\s+/g, ''),
    name.toLowerCase().replace(/\.+/g, 'dot'),
    name.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/js$/i, 'javascript'),
  ];

  // Remove duplicates
  const uniqueVariations = [...new Set(variations)];

  for (const variation of uniqueVariations) {
    const testIcon = `${source}:${variation}`;
    if (isValidIconId(testIcon)) {
      return testIcon;
    }
  }

  // Try some common mappings
  const commonMappings: Record<string, string> = {
    'nodejs': 'nodedotjs',
    'node': 'nodedotjs',
    'js': 'javascript',
    'ts': 'typescript',
    'gcp': 'googlecloud',
    'k8s': 'kubernetes',
    'postgres': 'postgresql',
    'mongo': 'mongodb',
    'mysql': 'mysql',
    'redis': 'redis',
    'nginx': 'nginx',
    'docker': 'docker',
    'kafka': 'apachekafka',
    'react': 'react',
    'vue': 'vuedotjs',
    'angular': 'angular',
    'next': 'nextdotjs',
    'nuxt': 'nuxt',
  };

  for (const [key, value] of Object.entries(commonMappings)) {
    if (name.toLowerCase().includes(key)) {
      const testIcon = `${source}:${value}`;
      if (isValidIconId(testIcon)) {
        return testIcon;
      }
    }
  }

  return null;
}

/**
 * Batch validate multiple icons
 */
export function validateIcons(iconIds: string[]): IconValidationResult[] {
  return iconIds.map(validateAndNormalizeIcon);
}

/**
 * Get a fallback icon based on category
 */
export function getFallbackIconForCategory(category?: string): string {
  const fallbacks: Record<string, string> = {
    frontend: 'si:react',
    backend: 'si:nodedotjs',
    database: 'si:postgresql',
    infrastructure: 'mdi:server',
    devops: 'si:docker',
    security: 'mdi:shield-lock',
    monitoring: 'si:grafana',
    storage: 'mdi:harddisk',
    network: 'mdi:router',
    cloud: 'mdi:cloud',
  };

  return fallbacks[category || ''] || 'si:files';
}

/**
 * Clear the validation cache (useful for testing)
 */
export function clearIconValidationCache(): void {
  iconValidationCache.clear();
}
